import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
    Coins, AlertCircle, AlertTriangle, CheckCircle, History, ArrowRight, RotateCcw,
} from 'lucide-react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { resolveFunctionsTarget } from '../utils/environmentConfig';
import { trackEvent, trackException, getCurrentTraceId } from '../services/appInsightsService';
import usageEconomics from '../data/usageEconomics.json';
import './UsageCatalogSection.css';

/**
 * UsageCatalogSection — Admin Settings > Plans. The platform editor for the usage-credit
 * catalog: the points charged per unit of work, the monthly/trial point grants, and the
 * economics guardrail that says whether a rate still clears its own cost.
 *
 * Sprint 12 of plans/Credit-Pricing-Redesign-Plan.md, against the write path as actually
 * delivered by `src/Functions/Functions/Admin/UsageCatalogAdminFunctions.cs`. It reuses
 * MeetingLimitsSection's shape (dirty edit -> sticky "N unsaved changes" bar -> modal diff +
 * required reason -> save) but differs from that precedent in three ways that are easy to get
 * silently wrong:
 *
 *   1. Error bodies here are `{ errorCode, error }`, never `{ code, message }`. Reading
 *      `body.message` would render `undefined`, fall back to a generic string, and hide the
 *      server's real explanation from the operator.
 *   2. Saving is TWO calls. PUT stores a DRAFT (no consumer reads a draft), and only once that
 *      succeeds does POST /publish flip it live. A publish failure therefore leaves a genuinely
 *      saved draft on screen — reverting the display would tell the operator their edit was lost
 *      when it is stored and only unpublished.
 *   3. Publish's 200 body carries version names, NOT the document. The panel reloads after a
 *      successful publish rather than rendering a document it never received.
 *
 * Rates are MILLI on the wire and POINTS in the UI (1 point = 1000 milli). Points are
 * fractional — `meeting.extraParticipantMinute` is 60 milli = 0.06 points — so every conversion
 * parses a float and rounds only at the milli boundary.
 *
 * The guardrail panel divides a COMMITTED artifact and never recomputes a cost. The cost model
 * is owned by `credit_pricing_model.py` (D3.8 A-4: "중복 구현 금지"); a second implementation in
 * the browser would create a second truth. Editing a provider price here therefore changes no
 * number at all — it marks the snapshot stale and names the rerun.
 *
 * English copy only (AdminPortal's recorded localization exception) — this portal carries no
 * i18n framework.
 */

const CATALOG_PATH = '/platform/usage-catalog';
const VALIDATE_PATH = '/platform/usage-catalog/validate';
const PUBLISH_PATH = '/platform/usage-catalog/publish';

/** 1 point = 1000 milli (D3.1 §1.4). The wire is milli; the operator edits points. */
const MILLI_PER_POINT = 1000;

/** The server's own reason bounds (UsageCatalogValidator.cs:99-102). */
const MIN_REASON_LENGTH = 5;

/**
 * Shown when publish has no precondition to send — the server's own 428 wording, reused so the
 * operator reads the same sentence whether the ETag was missing here or refused there.
 */
const PUBLISH_PRECONDITION_MISSING = 'Publishing needs the version of the draft that was just saved.';

/**
 * The rate keys in display order. This list orders what the SERVER sends; it never adds a key.
 * An unknown key is rejected by the server with 400, so a key this UI invented could not be
 * saved — and a key the server sends that is missing here is surfaced (below the ordered ones)
 * rather than silently dropped from the table and from the next PUT.
 */
const RATE_KEY_ORDER = [
    'realtime.conversation', 'realtime.transcription', 'realtime.simultaneous', 'realtime.normal',
    'realtime.normal.partials', 'realtime.advanced', 'realtime.advanced.partials',
    'content.audioMinute', 'content.translationMinuteLanguage', 'content.documentUnitLanguage',
    'capture.translationUnitLanguage', 'tts.outputMinute',
    'meetingAi.unit.s', 'meetingAi.unit.m', 'meetingAi.unit.l', 'meetingAi.unit.xl',
    'capture.comprehendUnit', 'meeting.extraParticipantMinute',
];

/** What one unit of each rate key actually is, for the operator. Display only. */
const RATE_UNITS = {
    'realtime.conversation': 'Two-Way minute',
    'realtime.transcription': 'minute',
    'realtime.simultaneous': 'minute × language',
    'realtime.normal': 'minute × language',
    'realtime.normal.partials': 'minute × language',
    'realtime.advanced': 'minute × language',
    'realtime.advanced.partials': 'minute × language',
    'content.audioMinute': 'minute',
    'content.translationMinuteLanguage': 'minute × language',
    'content.documentUnitLanguage': 'unit × language',
    'capture.translationUnitLanguage': 'unit × language',
    'tts.outputMinute': 'output minute',
    // #3703: one Meeting AI call is priced by the SIZE of the transcript it sends, because every
    // call sends the whole thing. The unit label names the band ceiling the operator is editing.
    'meetingAi.unit.s': 'call, transcript ≤ 8K tokens (~30 min)',
    'meetingAi.unit.m': 'call, transcript ≤ 32K tokens (~2 h)',
    'meetingAi.unit.l': 'call, transcript ≤ 80K tokens (~5 h)',
    'meetingAi.unit.xl': 'call, transcript > 80K tokens',
    'capture.comprehendUnit': 'unit',
    'meeting.extraParticipantMinute': 'extra participant × minute',
};

/** Fix 2 badge copy — exact strings pinned by UsageCatalogSection.test.js. Never color-only. */
const EXTRA_PARTICIPANT_OFF_BADGE = 'Not metered in this environment (BILLING_EXTRA_PARTICIPANT_METERING_ENABLED off). Joins are refused at the included count.';
const EXTRA_PARTICIPANT_ON_BADGE = 'Metered above the included count, joins allowed up to the hard cap.';

/** Returns the badge text for one rate row, or null when none applies. The extra-language
 * environment switch is gone (one-minute pricing): languages are priced by the per-plan percent
 * table, so only the participant row carries a switch badge. */
function rateBadgeText(key, meteringSwitches) {
    if (!meteringSwitches) return null;
    if (key === 'meeting.extraParticipantMinute') {
        return meteringSwitches.extraParticipant ? EXTRA_PARTICIPANT_ON_BADGE : EXTRA_PARTICIPANT_OFF_BADGE;
    }
    return null;
}

// The plans whose extra-language percent and included units this form edits (and lists first). The
// Meetings Platform plans (2026-11-03) are named below so their monthly grant row — the meeting
// wall-minute pool — reads by name; their 0 % entry is carried through untouched on every PUT.
const PLAN_GRANT_ORDER = ['plan_meeting_v2', 'plan_meeting_pro_v2'];
const PLAN_NAMES = {
    plan_meeting_v2: 'Meeting',
    plan_meeting_pro_v2: 'Meeting Pro',
    plan_meetings_starter_v1: 'Meetings Starter',
    plan_meetings_business_v1: 'Meetings Business',
    plan_free_v2: 'Free',
};
const FREE_PLAN_ID = 'plan_free_v2';

/** The guardrail verdicts, as literal operator-facing text. Never colour alone. */
const VERDICT_OK = 'OK';
const VERDICT_BELOW_COST = 'Below cost x 1.3';
const VERDICT_NOT_GRADED = 'Not graded';
const VERDICT_INVALID = 'Enter a number';

/** `UsageCatalogCompiledDefault.LegacyExtraLanguagePercent`: a document without the field prices
 *  every plan's extra languages at 100 % (linear ×N), so that is what the row shows. */
const LEGACY_EXTRA_LANGUAGE_PERCENT = 100;
const MAX_EXTRA_LANGUAGE_PERCENT = 100;

/** `UsageCatalogValidator.KnownPlanIds` — every plan a percent may be keyed to. A pre-v6 document
 *  prices all of them at 100 %, and the resolver reads an unlisted plan at 100 % too (review M2), so
 *  seeding them at 100 on an edit only makes the stored table explicit; it never moves a price. */
const KNOWN_PLAN_IDS = [
    'plan_free_v2', 'plan_meeting_v2', 'plan_meeting_pro_v2',
    'plan_webinar_v2', 'plan_webinar_100_v2', 'plan_webinar_300_v2', 'plan_unlimited_v1',
];

/** `UsageCatalogValidator.IncludableRateKeys` — `meetingAi.unit.xl` is forbidden, so never offered. */
const INCLUDED_UNIT_KEYS = [
    'meetingAi.unit.s', 'meetingAi.unit.m', 'meetingAi.unit.l',
    'capture.translationUnitLanguage', 'capture.comprehendUnit', 'content.documentUnitLanguage',
];

/** Billed languages for a three-language session at `percent`: 1 + 2 × percent / 100. */
const threeLanguageWeight = (percent) => 1 + (2 * percent) / 100;

const languageWeightText = (weight) => `billed as ${weight} ${weight === 1 ? 'language' : 'languages'}`;

/** A whole, non-negative number typed into an input, or NaN. */
const toWholeNumber = (text) => (/^\d+$/.test(String(text ?? '').trim()) ? Number(String(text).trim()) : Number.NaN);

/** The percent each editable plan prices at, as the server reads the document. */
const languagePercentsOf = (catalogDocument) => PLAN_GRANT_ORDER.reduce((out, planId) => {
    const stored = catalogDocument.extraLanguagePercent;
    out[planId] = stored == null ? LEGACY_EXTRA_LANGUAGE_PERCENT : (stored[planId] ?? 0);
    return out;
}, {});

/** Every editable plan × includable key, 0 where the document grants nothing. */
const includedUnitsOf = (catalogDocument) => PLAN_GRANT_ORDER.reduce((out, planId) => {
    const plan = (catalogDocument.monthlyIncludedUnits || {})[planId] || {};
    out[planId] = INCLUDED_UNIT_KEYS.reduce((units, key) => {
        units[key] = plan[key] ?? 0;
        return units;
    }, {});
    return out;
}, {});

const mapIncluded = (included, project) => mapValues(included, (units) => mapValues(units, project));

const FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), '
    + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The only real production Functions host — everything else reads as Staging, so a
 *  misconfigured environment can never be mistaken for production. */
const isProductionHost = (baseUrl) => {
    try {
        return new URL(baseUrl).hostname.toLowerCase() === 'func.qolsync.ai';
    } catch (e) {
        return false;
    }
};

/** Never send an empty X-Trace-Id — a money write that arrives uncorrelated cannot be tied back
 *  to its server-side audit entry. */
const traceIdHeader = () => getCurrentTraceId() || `adminportal-${Date.now().toString(36)}`;

const toPointsText = (milli) => String(Number(milli || 0) / MILLI_PER_POINT);

/** Points are fractional, so this parses a float and rounds only at the milli boundary. A
 *  `parseInt` here would silently turn 0.3 points into 0 milli — a free rate. */
const toMilli = (pointsText) => {
    const points = Number.parseFloat(pointsText);
    return Number.isFinite(points) ? Math.round(points * MILLI_PER_POINT) : Number.NaN;
};

/** `UsageCatalogValidator.EffectiveAtPastToleranceMinutes`: the server refuses an effective time
 *  more than this far in the past (EFFECTIVE_AT_IN_PAST). The rule stays server-side; this only
 *  says so before the operator spends a Review on a guaranteed 400 (#6459). */
const EFFECTIVE_AT_PAST_TOLERANCE_MS = 5 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const EFFECTIVE_AT_PAST_MESSAGE = 'This time is in the past, so the server would refuse it '
    + '(new starts would already have been priced). Choose Now or a later time.';

const toUtcIso = (date) => date.toISOString().replace(/\.\d{3}Z$/, 'Z');

/** Now, rounded UP to the next whole minute, as the UTC ISO string the server stores. */
export const nextWholeMinuteIso = (nowMs) => toUtcIso(new Date(Math.ceil(nowMs / MINUTE_MS) * MINUTE_MS));

const pad2 = (n) => String(n).padStart(2, '0');

/** UTC ISO -> the `datetime-local` value (the operator's local wall time, minute precision). */
export const utcIsoToLocalInput = (iso) => {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** `datetime-local` value -> UTC ISO. A date-time string with no offset parses as LOCAL time. */
export const localInputToUtcIso = (value) => {
    const d = new Date(value);
    return !value || Number.isNaN(d.getTime()) ? '' : toUtcIso(d);
};

/** `2026-10-30T16:15:00Z` -> `2026-10-30 16:15 UTC`. */
export const formatUtcLabel = (iso) => {
    const d = new Date(iso);
    return !iso || Number.isNaN(d.getTime()) ? 'not set' : `${toUtcIso(d).slice(0, 16).replace('T', ' ')} UTC`;
};

/**
 * The draft's starting effective time. A published time already in the past would 400 on save,
 * so the draft follows the clock instead (`effectiveAtFollowsNow`: the request carries the minute
 * of the actual save, not of page load). A future scheduled time is kept as-is.
 */
const initialEffectiveAt = (publishedIso, nowMs) => {
    const published = Date.parse(publishedIso);
    if (Number.isFinite(published) && published >= nowMs) {
        return { effectiveAtUtc: publishedIso, effectiveAtFollowsNow: false };
    }
    return { effectiveAtUtc: nextWholeMinuteIso(nowMs), effectiveAtFollowsNow: true };
};

/** The value the request carries: a "now" draft is re-stamped at save time. */
const effectiveAtToSend = (draft, nowMs) => (
    draft.effectiveAtFollowsNow ? nextWholeMinuteIso(nowMs) : draft.effectiveAtUtc);

const effectiveAtRefused = (draft, nowMs) => {
    if (!draft || draft.effectiveAtFollowsNow) return false;
    const at = Date.parse(draft.effectiveAtUtc);
    return !Number.isFinite(at) || at < nowMs - EFFECTIVE_AT_PAST_TOLERANCE_MS;
};

const mapValues = (source, project) => Object.keys(source || {}).reduce((out, key) => {
    out[key] = project(source[key]);
    return out;
}, {});

/** `UsageCatalogValidator.VersionNamePattern`, with the revision number captured. */
const VERSION_NAME_PATTERN = /^usage-(catalog|rates)-v([0-9]+)$/;

/**
 * The next revision of a well-formed version name: `usage-rates-v4` -> `usage-rates-v5`.
 *
 * A name that does not match the server's pattern is returned UNCHANGED rather than decorated
 * into something like `usage-rates-v4-1`: that would turn one server violation
 * (VERSION_NAME_MALFORMED, which names the real problem) into a different one the operator
 * cannot act on. A malformed name is the server's V9 to report, not this panel's to paper over.
 */
const nextVersionName = (name) => {
    const match = VERSION_NAME_PATTERN.exec(name || '');
    if (!match) return name;
    return `usage-${match[1]}-v${Number(match[2]) + 1}`;
};

/**
 * Mirrors `UsageCatalogAdminFunctions.ResolvePublishedReference`: the stored document itself
 * when it is published, a projection of its `previous` snapshot when it is a draft with a known
 * prior revision, otherwise null. The comparison target for the version-reuse rule is the
 * PUBLISHED table — while a draft is pending, the live rates live only in `previous`, so
 * comparing against the draft's own values would both false-positive on a re-save and
 * false-negative on a candidate that reuses the actually-live rate identity.
 */
const publishedReference = (catalogDocument) => {
    if (!catalogDocument) return null;
    if (String(catalogDocument.status).toLowerCase() === 'published') return catalogDocument;
    if (!catalogDocument.previous) return null;
    return {
        catalogVersion: catalogDocument.previous.catalogVersion,
        rateVersion: catalogDocument.previous.rateVersion,
        ratesMilli: catalogDocument.previous.ratesMilli,
    };
};

/** Mirrors `UsageCatalogValidator.RateTablesMatch`: same key set, same values. Grants and the
 *  trial grant never enter it — which is why a grant-only edit may legally reuse a name. */
const rateTablesMatch = (candidate, published) => {
    const left = candidate || {};
    const right = published || {};
    const leftKeys = Object.keys(left);
    if (leftKeys.length !== Object.keys(right).length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
};

/** Orders the SERVER's keys by RATE_KEY_ORDER, keeping anything it sent that this build does
 *  not know about (surfaced, not dropped). */
const orderedRateKeys = (rates) => {
    const present = Object.keys(rates || {});
    const known = RATE_KEY_ORDER.filter((key) => present.includes(key));
    const unknown = present.filter((key) => !RATE_KEY_ORDER.includes(key));
    return [...known, ...unknown];
};

const orderedGrantPlanIds = (grants) => {
    const present = Object.keys(grants || {});
    const known = PLAN_GRANT_ORDER.filter((id) => present.includes(id));
    const rest = present.filter((id) => !PLAN_GRANT_ORDER.includes(id));
    return [...known, ...rest];
};

const planName = (planId) => PLAN_NAMES[planId] || planId;

const formatUsd = (value) => (Number.isFinite(value) ? `$${value.toFixed(5)}` : '—');

const formatPercent = (value) => (Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '—');

const formatTimestamp = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

/**
 * The panel's ENTIRE arithmetic: a division of the committed artifact. `unitCostUsd` comes from
 * the model's published R3 artifact and is never recomputed here (D3.8 A-4).
 */
const costPerPoint = (key, shape, points) => {
    const cost = usageEconomics.unitCostUsd[key];
    if (!cost || !Number.isFinite(points) || points <= 0) return Number.NaN;
    return cost[shape] / points;
};

/**
 * `points * grossPricePerPoint < unitCost[shape] * buffer`, checked against EVERY gating shape.
 * `aggressive_all_fallback` lives in `displayOnlyShapes` and is deliberately excluded — it is a
 * tail risk the model does not price against, so it must never drive a verdict.
 */
const rateVerdict = (key, points) => {
    const cost = usageEconomics.unitCostUsd[key];
    if (!cost) return VERDICT_NOT_GRADED;
    if (!Number.isFinite(points)) return VERDICT_INVALID;
    const revenue = points * usageEconomics.subscriptionGrossPricePerPointUsd;
    const breached = usageEconomics.gatingShapes.some(
        (shape) => revenue < cost[shape] * usageEconomics.thresholds.buffer,
    );
    return breached ? VERDICT_BELOW_COST : VERDICT_OK;
};

// --error/--warning/--success are the SAME hex in both themes, so neither can serve as TEXT on a
// token-tinted background (established pattern: PlanEditor.js noticeStyle,
// RealtimeProviderPolicySettings.js). The token tints the background and colours the icon; the
// text stays on --text-primary, which already flips per theme.
const noticeStyle = (tone) => ({
    margin: '0 0 var(--space-4)',
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-2)',
    background: `color-mix(in srgb, var(--${tone}) 12%, var(--bg-card))`,
    color: 'var(--text-primary)',
});

const noticeIconStyle = (tone) => ({ color: `var(--${tone})`, flexShrink: 0 });

const UsageCatalogSection = ({ readOnly = false }) => {
    const [catalog, setCatalog] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState(null);
    const [dialogError, setDialogError] = useState(null);
    const [validation, setValidation] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [toast, setToast] = useState(null);
    const [draftSaved, setDraftSaved] = useState(false);
    const [providerPrices, setProviderPrices] = useState(
        () => usageEconomics.providerPrices.reduce((out, price) => {
            out[price.name] = String(price.value);
            return out;
        }, {}),
    );

    const dialogRef = useRef(null);
    const reasonRef = useRef(null);
    const reviewBtnRef = useRef(null);
    const toastTimerRef = useRef(null);
    // Made `inert` (not unmounted, not `display:none`) while the dialog is open — see the effect.
    const backgroundRef = useRef(null);
    // Deferred so a `.focus()` at close time never races the re-render that re-enables the
    // Review button: an inert element cannot take focus until the attribute is off the DOM.
    const pendingFocusReturnRef = useRef(false);

    const target = useMemo(() => resolveFunctionsTarget(
        process.env.REACT_APP_FUNCTIONS_URL,
        process.env.REACT_APP_WEBSOCKET_URL,
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
    ), []);
    const envLabel = isProductionHost(target.baseUrl) ? 'Production' : 'Staging';
    const endpoint = `${target.baseUrl}${CATALOG_PATH}`;

    const load = useCallback(async () => {
        // An unresolved target is the hidden staging fallback, not a deliberate backend — do not
        // send this console's JWT to it (identical guard to RealtimeProviderPolicySettings.js).
        if (!target.resolved) return;

        setIsLoading(true);
        try {
            const response = await fetchWithAuth(endpoint, {
                method: 'GET',
                headers: { 'X-Trace-Id': traceIdHeader() },
            });
            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || `Failed to load the usage catalog (HTTP ${response.status}).`);
            }
            const payload = await response.json();
            // Named `catalogDocument`, never `document`: a local named `document` would shadow the
            // DOM global for the whole function body.
            const catalogDocument = payload && payload.document;
            if (!catalogDocument || !catalogDocument.ratesMilli) {
                throw new Error('The usage catalog response did not include a catalog document.');
            }
            setCatalog({
                document: catalogDocument,
                source: payload.source,
                etag: payload.etag,
                enginePropagationSeconds: payload.enginePropagationSeconds,
                meteringSwitches: payload.meteringSwitches,
                rates: { ...catalogDocument.ratesMilli },
                grants: { ...(catalogDocument.monthlyGrantMilli || {}) },
                trialGrant: catalogDocument.trialGrantMilli,
                languagePercents: languagePercentsOf(catalogDocument),
                included: includedUnitsOf(catalogDocument),
            });
            setDraft({
                rates: mapValues(catalogDocument.ratesMilli, toPointsText),
                grants: mapValues(catalogDocument.monthlyGrantMilli || {}, toPointsText),
                trialGrant: toPointsText(catalogDocument.trialGrantMilli),
                languagePercents: mapValues(languagePercentsOf(catalogDocument), String),
                included: mapIncluded(includedUnitsOf(catalogDocument), String),
                ...initialEffectiveAt(catalogDocument.effectiveAtUtc, Date.now()),
            });
            setDraftSaved(false);
            setLoadErrorMessage(null);
        } catch (err) {
            trackException(err, { errorType: 'UsageCatalog_LoadFailed' });
            // A failed load renders NO editor — a default table shown here would present rates
            // nobody read as if they were the live ones.
            setCatalog(null);
            setDraft(null);
            setLoadErrorMessage(err.message || 'Failed to load the usage catalog.');
        } finally {
            setIsLoading(false);
        }
    }, [endpoint, target.resolved]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!reviewOpen && pendingFocusReturnRef.current) {
            pendingFocusReturnRef.current = false;
            reviewBtnRef.current?.focus();
        }
    }, [reviewOpen]);

    // `inert` set imperatively, not as a JSX boolean prop: React 18 does not model it as a
    // recognized DOM property, so setAttribute/removeAttribute is the unambiguous form. `inert`
    // rather than `display:none` because unmounting the background reflows the page under the
    // dialog (a documented past bug in MeetingLimitsSection).
    useEffect(() => {
        const el = backgroundRef.current;
        if (!el) return;
        if (reviewOpen) {
            el.setAttribute('inert', '');
        } else {
            el.removeAttribute('inert');
        }
    }, [reviewOpen]);

    useEffect(() => {
        if (reviewOpen) reasonRef.current?.focus();
    }, [reviewOpen]);

    useEffect(() => () => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    }, []);

    const editable = !readOnly && target.resolved && !isSaving;

    const updateRate = (key, value) => {
        if (!editable) return;
        setDraft((prev) => ({ ...prev, rates: { ...prev.rates, [key]: value } }));
    };

    const updateGrant = (planId, value) => {
        if (!editable) return;
        setDraft((prev) => ({ ...prev, grants: { ...prev.grants, [planId]: value } }));
    };

    const updateTrialGrant = (value) => {
        if (!editable) return;
        setDraft((prev) => ({ ...prev, trialGrant: value }));
    };

    const updateLanguagePercent = (planId, value) => {
        if (!editable) return;
        setDraft((prev) => ({ ...prev, languagePercents: { ...prev.languagePercents, [planId]: value } }));
    };

    const updateIncluded = (planId, key, value) => {
        if (!editable) return;
        setDraft((prev) => ({
            ...prev,
            included: { ...prev.included, [planId]: { ...prev.included[planId], [key]: value } },
        }));
    };

    const updateEffectiveAt = (localValue) => {
        if (!editable) return;
        setDraft((prev) => ({ ...prev, effectiveAtUtc: localInputToUtcIso(localValue), effectiveAtFollowsNow: false }));
    };

    const setEffectiveAtNow = () => {
        if (!editable) return;
        setDraft((prev) => ({ ...prev, effectiveAtUtc: nextWholeMinuteIso(Date.now()), effectiveAtFollowsNow: true }));
    };

    const updateProviderPrice = (name, value) => {
        if (!editable) return;
        setProviderPrices((prev) => ({ ...prev, [name]: value }));
    };

    const resetProviderPrices = () => {
        setProviderPrices(usageEconomics.providerPrices.reduce((out, price) => {
            out[price.name] = String(price.value);
            return out;
        }, {}));
    };

    const providerPricesAreStale = usageEconomics.providerPrices.some(
        (price) => Number.parseFloat(providerPrices[price.name]) !== price.value,
    );

    /** One diff row per changed FIELD. A rate row's field id is the bare rate key. */
    const changedFields = () => {
        if (!catalog || !draft) return [];
        const rows = [];
        orderedRateKeys(draft.rates).forEach((key) => {
            const from = catalog.rates[key];
            const to = toMilli(draft.rates[key]);
            if (to !== from) {
                rows.push({
                    field: key,
                    label: key,
                    fromText: toPointsText(from),
                    toText: Number.isNaN(to) ? '—' : toPointsText(to),
                });
            }
        });
        orderedGrantPlanIds(draft.grants).forEach((planId) => {
            const from = catalog.grants[planId];
            const to = toMilli(draft.grants[planId]);
            if (to !== from) {
                rows.push({
                    field: `monthlyGrantMilli.${planId}`,
                    label: `${planName(planId)} monthly grant`,
                    fromText: toPointsText(from),
                    toText: Number.isNaN(to) ? '—' : toPointsText(to),
                });
            }
        });
        PLAN_GRANT_ORDER.forEach((planId) => {
            const from = catalog.languagePercents[planId];
            const to = toWholeNumber(draft.languagePercents[planId]);
            if (to !== from) {
                rows.push({
                    field: `extraLanguagePercent.${planId}`,
                    label: `${planName(planId)} extra-language percent`,
                    fromText: `${from}%`,
                    toText: Number.isNaN(to) ? '—' : `${to}%`,
                });
            }
        });
        PLAN_GRANT_ORDER.forEach((planId) => INCLUDED_UNIT_KEYS.forEach((key) => {
            const from = catalog.included[planId][key];
            const to = toWholeNumber(draft.included[planId][key]);
            if (to !== from) {
                rows.push({
                    field: `monthlyIncludedUnits.${planId}.${key}`,
                    label: `${planName(planId)} included ${key}`,
                    fromText: String(from),
                    toText: Number.isNaN(to) ? '—' : String(to),
                });
            }
        }));
        const trialTo = toMilli(draft.trialGrant);
        if (trialTo !== catalog.trialGrant) {
            rows.push({
                field: 'trialGrantMilli',
                label: 'Trial grant',
                fromText: toPointsText(catalog.trialGrant),
                toText: Number.isNaN(trialTo) ? '—' : toPointsText(trialTo),
            });
        }
        // A "now" draft is not an operator edit (it only replaces a past time the server would
        // refuse), so it is shown with the version bumps below rather than counted here.
        const effectiveRow = effectiveAtRow();
        if (effectiveRow && !draft.effectiveAtFollowsNow) rows.push(effectiveRow);
        return rows;
    };

    function effectiveAtRow() {
        const sent = effectiveAtToSend(draft, Date.now());
        if (sent === (catalog.document.effectiveAtUtc || '')) return null;
        return {
            field: 'effectiveAtUtc',
            label: 'Effective at (UTC)',
            fromText: formatUtcLabel(catalog.document.effectiveAtUtc),
            toText: draft.effectiveAtFollowsNow ? `Now — ${formatUtcLabel(sent)}` : formatUtcLabel(sent),
        };
    }

    const editedFields = changedFields();
    // The version bump below is a CONSEQUENCE of those edits, not one of them, so it is shown in
    // the diff but never counted: "1 unsaved change" must still mean the one rate the operator
    // actually touched.
    const changeCount = editedFields.length;
    const anyDirty = changeCount > 0;

    // Each published name must identify one rate table; retain names already bumped in a draft.
    const proposedVersion = (field) => {
        if (!catalog || !draft) return '';
        const loaded = catalog.document[field];
        const published = publishedReference(catalog.document);
        if (!published || loaded !== published[field]) return loaded;
        if (rateTablesMatch(mapValues(draft.rates, toMilli), published.ratesMilli)) return loaded;
        return nextVersionName(loaded);
    };
    const proposedVersions = {
        catalogVersion: proposedVersion('catalogVersion'),
        rateVersion: proposedVersion('rateVersion'),
    };
    const versionDiffRows = Object.entries(proposedVersions)
        .filter(([field, value]) => catalog && value !== catalog.document[field])
        .map(([field, value]) => ({
            field,
            label: field === 'catalogVersion' ? 'Catalog version' : 'Rate version',
            fromText: catalog.document[field],
            toText: value,
        }));
    const followsNowRow = catalog && draft && draft.effectiveAtFollowsNow ? effectiveAtRow() : null;
    const diffRows = [...editedFields, ...(followsNowRow ? [followsNowRow] : []), ...versionDiffRows];
    // Blocks Review/Save rather than silently moving a time the operator picked: on a price
    // change a quiet re-date is worse than one click on Now (#6459).
    const effectiveAtBlocked = effectiveAtRefused(draft, Date.now());

    const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;
    const everyValueParses = draft
        ? [
            ...Object.values(draft.rates).map(toMilli),
            ...Object.values(draft.grants).map(toMilli),
            toMilli(draft.trialGrant),
            ...Object.values(draft.included).flatMap((units) => Object.values(units).map(toWholeNumber)),
        ].every((milli) => Number.isFinite(milli))
            && Object.values(draft.languagePercents)
                .map(toWholeNumber)
                .every((percent) => percent >= 0 && percent <= MAX_EXTRA_LANGUAGE_PERCENT)
        : false;

    // Only an EDITED percent or included table is rebuilt: an untouched one rides along exactly as
    // loaded, so a pre-v6 document (no field = 100 % for every plan, nothing included) keeps its
    // shape. Plans and keys this editor does not show are preserved, never dropped.
    const percentsEdited = () => PLAN_GRANT_ORDER.some(
        (planId) => toWholeNumber(draft.languagePercents[planId]) !== catalog.languagePercents[planId],
    );
    const includedEdited = () => PLAN_GRANT_ORDER.some((planId) => INCLUDED_UNIT_KEYS.some(
        (key) => toWholeNumber(draft.included[planId][key]) !== catalog.included[planId][key],
    ));
    const extraLanguagePercentToSend = () => {
        if (!percentsEdited()) return catalog.document.extraLanguagePercent ?? undefined;
        const stored = catalog.document.extraLanguagePercent
            ?? KNOWN_PLAN_IDS.reduce((out, planId) => ({ ...out, [planId]: LEGACY_EXTRA_LANGUAGE_PERCENT }), {});
        return { ...stored, ...mapValues(draft.languagePercents, toWholeNumber) };
    };
    const includedUnitsToSend = () => {
        if (!includedEdited()) return catalog.document.monthlyIncludedUnits ?? undefined;
        const out = { ...(catalog.document.monthlyIncludedUnits || {}) };
        PLAN_GRANT_ORDER.forEach((planId) => {
            const units = { ...(out[planId] || {}) };
            INCLUDED_UNIT_KEYS.forEach((key) => {
                const count = toWholeNumber(draft.included[planId][key]);
                if (count > 0) units[key] = count;
                else delete units[key];
            });
            out[planId] = units;
        });
        return out;
    };

    const buildRequestBody = (reasonText) => ({
        reason: reasonText,
        ...proposedVersions,
        currency: catalog.document.currency,
        effectiveAtUtc: effectiveAtToSend(draft, Date.now()),
        ratesMilli: mapValues(draft.rates, toMilli),
        monthlyGrantMilli: mapValues(draft.grants, toMilli),
        trialGrantMilli: toMilli(draft.trialGrant),
        // usage-rates-v6 fields. undefined (an untouched pre-v6 document) drops out of
        // JSON.stringify, which keeps that document's shape.
        extraLanguagePercent: extraLanguagePercentToSend(),
        monthlyIncludedUnits: includedUnitsToSend(),
    });

    const closeDialog = (returnFocus) => {
        pendingFocusReturnRef.current = returnFocus;
        setReviewOpen(false);
        setDialogError(null);
    };

    const handleDiscard = () => {
        if (!catalog) return;
        setDraft({
            rates: mapValues(catalog.rates, toPointsText),
            grants: mapValues(catalog.grants, toPointsText),
            trialGrant: toPointsText(catalog.trialGrant),
            languagePercents: mapValues(catalog.languagePercents, String),
            included: mapIncluded(catalog.included, String),
            ...initialEffectiveAt(catalog.document.effectiveAtUtc, Date.now()),
        });
        setReason('');
        setValidation(null);
        closeDialog(false);
    };

    const handleDialogKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            // Refused while a save is in flight, the same guard the Cancel button already
            // carries. `dialogError` renders ONLY inside this dialog and the failure path
            // raises no toast, so dismissing it mid-write turns a failed money write into
            // silence: the PUT/publish still resolve, against a screen that no longer says so.
            // The keystroke is still swallowed rather than passed on — it was aimed here.
            if (!isSaving) closeDialog(true);
            return;
        }
        if (e.key !== 'Tab' || !dialogRef.current) return;
        const focusable = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const showToast = (message) => {
        setToast(message);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setToast(null), 2400);
    };

    /** Reads the delivered error shape — `{ errorCode, error }`, never `{ code, message }`. */
    const errorTextOf = (body, fallback) => {
        const violations = Array.isArray(body.violations) && body.violations.length > 0
            ? ` (${body.violations.join(', ')})`
            : '';
        return `${body.error || fallback}${violations}`;
    };

    const handleValidate = async () => {
        if (!catalog || !draft || !target.resolved || readOnly || isValidating) return;

        setIsValidating(true);
        try {
            const response = await fetchWithAuth(`${target.baseUrl}${VALIDATE_PATH}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Trace-Id': traceIdHeader() },
                body: JSON.stringify(buildRequestBody(reason.trim())),
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                const message = errorTextOf(body, `Failed to validate the usage catalog (HTTP ${response.status}).`);
                trackException(new Error(message), {
                    errorType: 'UsageCatalog_ValidateFailed', errorCode: body.errorCode, status: response.status,
                });
                setValidation({ failed: true, message });
                return;
            }
            setValidation({
                failed: false,
                valid: body.valid,
                violations: Array.isArray(body.violations) ? body.violations : [],
                warnings: Array.isArray(body.warnings) ? body.warnings : [],
            });
        } catch (err) {
            trackException(err, { errorType: 'UsageCatalog_ValidateFailed' });
            setValidation({ failed: true, message: err.message || 'Failed to validate the usage catalog.' });
        } finally {
            setIsValidating(false);
        }
    };

    const handleSaveAndPublish = async () => {
        if (!catalog || !draft || !anyDirty || !everyValueParses || !reasonValid) return;
        if (effectiveAtRefused(draft, Date.now())) {
            setDialogError(EFFECTIVE_AT_PAST_MESSAGE);
            return;
        }
        if (!target.resolved || readOnly || isSaving) return;

        const reasonToSend = reason.trim();
        setDialogError(null);
        setIsSaving(true);
        try {
            const putResponse = await fetchWithAuth(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    // `*` only when nothing is stored yet (source is the compiled default); the
                    // server requires If-Match on EVERY write, because every write is a price change.
                    'If-Match': catalog.etag || '*',
                    'X-Trace-Id': traceIdHeader(),
                },
                body: JSON.stringify(buildRequestBody(reasonToSend)),
            });

            if (putResponse.status === 412 || putResponse.status === 428) {
                const body = await putResponse.json().catch(() => ({}));
                const fallback = putResponse.status === 412
                    ? 'The usage catalog changed since it was loaded. Reload it and apply the edit again.'
                    : 'The usage catalog needs the version you loaded before this can be applied.';
                const message = errorTextOf(body, fallback);
                trackException(new Error(message), {
                    errorType: 'UsageCatalog_SaveConflict', errorCode: body.errorCode, status: putResponse.status,
                });
                setDialogError(message);
                // The stale precondition guarantees a repeat failure, so the fresh document is
                // reloaded — which also returns the display to the last SAVED values.
                await load();
                return;
            }

            if (!putResponse.ok) {
                const body = await putResponse.json().catch(() => ({}));
                const message = errorTextOf(body, `Failed to save the usage catalog draft (HTTP ${putResponse.status}).`);
                trackException(new Error(message), {
                    errorType: 'UsageCatalog_SaveFailed', errorCode: body.errorCode, status: putResponse.status,
                });
                setDialogError(message);
                return;
            }

            const sentBody = buildRequestBody(reasonToSend);
            const saved = await putResponse.json().catch(() => ({}));
            // The draft is now stored. The edited values stay on screen and become the new
            // baseline; only the metadata and the new ETag come from the response, because the
            // authoritative document is re-read after publish, not from this echo.
            setCatalog((prev) => ({
                ...prev,
                document: {
                    ...prev.document,
                    extraLanguagePercent: sentBody.extraLanguagePercent ?? prev.document.extraLanguagePercent,
                    monthlyIncludedUnits: sentBody.monthlyIncludedUnits ?? prev.document.monthlyIncludedUnits,
                    ...(saved.document || {}),
                },
                etag: saved.etag || prev.etag,
                enginePropagationSeconds: saved.enginePropagationSeconds ?? prev.enginePropagationSeconds,
                rates: mapValues(draft.rates, toMilli),
                grants: mapValues(draft.grants, toMilli),
                trialGrant: toMilli(draft.trialGrant),
                languagePercents: mapValues(draft.languagePercents, toWholeNumber),
                included: mapIncluded(draft.included, toWholeNumber),
            }));
            setDraftSaved(true);

            // The ETag the PUT just returned is the ONLY value that can satisfy publish's
            // precondition, so its absence ends the flow here rather than inventing one.
            // `catalog.etag` is the closure's PRE-SAVE value (`setCatalog` is a state update) and
            // would 412 every publish, and `'*'` reads as "any version" — the blind overwrite this
            // precondition exists to prevent, which the server refuses on publish anyway. The draft
            // IS saved, so this takes the same path a 428 takes: say so, and reload a document that
            // carries a usable ETag to publish from.
            if (!saved.etag) {
                trackException(new Error(PUBLISH_PRECONDITION_MISSING), {
                    errorType: 'UsageCatalog_PublishConflict', errorCode: 'MISSING_SAVED_ETAG',
                });
                setDialogError(PUBLISH_PRECONDITION_MISSING);
                await load();
                return;
            }

            const publishResponse = await fetchWithAuth(`${target.baseUrl}${PUBLISH_PATH}`, {
                method: 'POST',
                headers: {
                    // Publish is a price change too, so the server requires If-Match here as well
                    // (428 when absent, 412 when stale).
                    'If-Match': saved.etag,
                    'X-Trace-Id': traceIdHeader(),
                },
            });

            if (publishResponse.status === 412 || publishResponse.status === 428) {
                const body = await publishResponse.json().catch(() => ({}));
                const fallback = publishResponse.status === 412
                    ? 'The saved draft changed before it could be published. Reload it and publish again.'
                    : PUBLISH_PRECONDITION_MISSING;
                const message = errorTextOf(body, fallback);
                trackException(new Error(message), {
                    errorType: 'UsageCatalog_PublishConflict',
                    errorCode: body.errorCode,
                    status: publishResponse.status,
                });
                setDialogError(message);
                // Same reasoning as the save conflict: a stale precondition guarantees a repeat
                // failure, so the authoritative document is re-read. The PUT succeeded, so this
                // reload shows the saved draft — it does not discard the operator's edit.
                await load();
                return;
            }

            if (!publishResponse.ok) {
                const body = await publishResponse.json().catch(() => ({}));
                const message = errorTextOf(body, `Failed to publish the saved draft (HTTP ${publishResponse.status}).`);
                trackException(new Error(message), {
                    errorType: 'UsageCatalog_PublishFailed', errorCode: body.errorCode, status: publishResponse.status,
                });
                // The PUT succeeded: the draft is stored and stays on screen. Reverting here
                // would tell the operator an edit that IS saved was lost.
                setDialogError(message);
                return;
            }

            const published = await publishResponse.json().catch(() => ({}));
            trackEvent('UsageCatalogPublished', {
                catalogVersion: published.catalogVersion,
                rateVersion: published.rateVersion,
                version: published.version,
                changedFieldCount: changeCount,
            });
            setReason('');
            setValidation(null);
            showToast('Draft saved and published');
            closeDialog(true);
            // Publish's 200 carries version names, not the document — reload rather than render
            // a document this panel never received.
            await load();
        } catch (err) {
            trackException(err, { errorType: 'UsageCatalog_SaveThrew' });
            setDialogError(err.message || 'Failed to save the usage catalog.');
        } finally {
            setIsSaving(false);
        }
    };

    // The static rate/grant template is shown when the backend target is unresolved so the
    // operator can see what this panel governs, with every control disabled and nothing fetched.
    // A failed LOAD is the other case and renders no editor at all.
    const showEditor = Boolean(draft) || !target.resolved;
    const rateKeys = draft ? orderedRateKeys(draft.rates) : RATE_KEY_ORDER;
    const grantPlanIds = draft ? orderedGrantPlanIds(draft.grants) : PLAN_GRANT_ORDER;
    const auditEntries = (catalog && catalog.document.audit) || [];
    const statusLabel = catalog && catalog.document.status === 'published' ? 'Published' : 'Draft';
    const inputsDisabled = readOnly || !target.resolved || !draft || isSaving;

    const pointsOf = (key) => (draft ? Number.parseFloat(draft.rates[key]) : Number.NaN);

    return (
        <section className="usage-catalog-section" aria-labelledby="usage-catalog-title">
            <div ref={backgroundRef} aria-hidden={reviewOpen || undefined}>
                <div className="usage-catalog-header">
                    <div className="usage-catalog-header-left">
                        <Coins size={20} aria-hidden="true" />
                        <div>
                            <h2 id="usage-catalog-title">Points catalog &amp; rates</h2>
                            <p>
                                How many points one minute, one document and one AI answer cost, and how
                                many points each plan is granted every month.
                            </p>
                        </div>
                    </div>
                    <div className="usage-catalog-chips">
                        <span className="usage-catalog-env-chip" title="Changes apply to this environment only">
                            {envLabel}
                        </span>
                        {catalog && <span className="usage-catalog-chip">{statusLabel}</span>}
                        {catalog && <span className="usage-catalog-chip">{catalog.source}</span>}
                    </div>
                </div>

                {catalog && (
                    <dl className="usage-catalog-meta">
                        <div>
                            <dt>Catalog version</dt>
                            <dd>{catalog.document.catalogVersion}</dd>
                        </div>
                        <div>
                            <dt>Rate version</dt>
                            <dd>{catalog.document.rateVersion}</dd>
                        </div>
                        <div>
                            <dt>Document version</dt>
                            <dd>{catalog.document.version}</dd>
                        </div>
                        <div>
                            <dt>Billing epoch</dt>
                            <dd>{catalog.document.billingEpoch || '—'}</dd>
                        </div>
                        <div>
                            <dt>Effective at (UTC)</dt>
                            <dd>{catalog.document.effectiveAtUtc || 'not set'}</dd>
                        </div>
                        <div>
                            <dt>Functions catalog cache TTL</dt>
                            <dd>{`${catalog.enginePropagationSeconds ?? 60} seconds; admitted sessions retain pinned rates`}</dd>
                        </div>
                    </dl>
                )}

                {readOnly && (
                    <p className="usage-catalog-status">
                        Read-only platform access: the catalog is shown but cannot be changed here.
                    </p>
                )}

                {!target.resolved && (
                    <div role="alert" style={noticeStyle('error')}>
                        <AlertCircle size={16} style={noticeIconStyle('error')} aria-hidden="true" />
                        <span>
                            No Functions backend is configured for this console, so the catalog was not read
                            and nothing can be saved.
                        </span>
                    </div>
                )}

                {loadErrorMessage && (
                    <div role="alert" style={noticeStyle('error')}>
                        <AlertCircle size={16} style={noticeIconStyle('error')} aria-hidden="true" />
                        <span>{loadErrorMessage}</span>
                    </div>
                )}

                {isLoading && !catalog && <p className="usage-catalog-status">Loading the points catalog…</p>}

                {showEditor && (
                    <>
                        <div className="usage-catalog-card">
                            <h3 id="usage-catalog-grants-heading">Monthly grant per plan</h3>
                            <p className="usage-catalog-hint">
                                Points added to every seat on the plan at the start of each quota period.
                                A change applies from the next period, never retroactively.
                            </p>
                            <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-grants-heading">
                                <table className="usage-catalog-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Plan</th>
                                            <th scope="col">Plan id</th>
                                            <th scope="col">Points</th>
                                            <th scope="col">Milli</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {grantPlanIds.map((planId) => (
                                            <tr key={planId} data-testid={`grant-row-${planId}`}>
                                                <th scope="row">
                                                    <label htmlFor={`grant-${planId}`}>{planName(planId)}</label>
                                                </th>
                                                <td><code>{planId}</code></td>
                                                <td className="usage-catalog-num">
                                                    <input
                                                        id={`grant-${planId}`}
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        value={draft ? draft.grants[planId] : ''}
                                                        onChange={(e) => updateGrant(planId, e.target.value)}
                                                        disabled={inputsDisabled}
                                                    />
                                                </td>
                                                <td className="usage-catalog-num">
                                                    {draft ? String(toMilli(draft.grants[planId])) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr data-testid="grant-row-trial">
                                            <th scope="row">
                                                <label htmlFor="trial-grant">Trial grant (3-day intro)</label>
                                            </th>
                                            <td><code>trialGrantMilli</code></td>
                                            <td className="usage-catalog-num">
                                                <input
                                                    id="trial-grant"
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    value={draft ? draft.trialGrant : ''}
                                                    onChange={(e) => updateTrialGrant(e.target.value)}
                                                    disabled={inputsDisabled}
                                                />
                                            </td>
                                            <td className="usage-catalog-num">
                                                {draft ? String(toMilli(draft.trialGrant)) : '—'}
                                            </td>
                                        </tr>
                                        {!grantPlanIds.includes(FREE_PLAN_ID) && (
                                            <tr data-testid={`grant-row-${FREE_PLAN_ID}`}>
                                                <th scope="row">Free</th>
                                                <td><code>{FREE_PLAN_ID}</code></td>
                                                <td className="usage-catalog-num">0</td>
                                                <td className="usage-catalog-num">0</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="usage-catalog-card" data-testid="usage-catalog-language-percent">
                            <h3 id="usage-catalog-language-percent-heading">Extra-language percent per plan</h3>
                            <p className="usage-catalog-hint">
                                What each target language after the first costs, as a share of one language.
                                0% bills a three-language meeting as one minute a minute; 100% bills it as three.
                                The broadcast (Webinar / Event Pass) axis always bills every language.
                                Sessions keep the percent they started with.
                            </p>
                            <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-language-percent-heading">
                                <table className="usage-catalog-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Plan</th>
                                            <th scope="col">Plan id</th>
                                            <th scope="col">Percent</th>
                                            <th scope="col">3 languages</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {PLAN_GRANT_ORDER.map((planId) => {
                                            const percent = draft ? toWholeNumber(draft.languagePercents[planId]) : Number.NaN;
                                            const inRange = percent >= 0 && percent <= MAX_EXTRA_LANGUAGE_PERCENT;
                                            return (
                                                <tr key={planId} data-testid={`language-percent-row-${planId}`}>
                                                    <th scope="row">
                                                        <label htmlFor={`language-percent-${planId}`}>{planName(planId)}</label>
                                                    </th>
                                                    <td><code>{planId}</code></td>
                                                    <td className="usage-catalog-num">
                                                        <input
                                                            id={`language-percent-${planId}`}
                                                            type="number"
                                                            min="0"
                                                            max={MAX_EXTRA_LANGUAGE_PERCENT}
                                                            step="1"
                                                            value={draft ? draft.languagePercents[planId] : ''}
                                                            onChange={(e) => updateLanguagePercent(planId, e.target.value)}
                                                            disabled={inputsDisabled}
                                                            aria-invalid={draft && !inRange ? 'true' : undefined}
                                                        />
                                                    </td>
                                                    <td className="usage-catalog-num">
                                                        {inRange ? languageWeightText(threeLanguageWeight(percent)) : 'Enter 0–100'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="usage-catalog-card" data-testid="usage-catalog-included-units">
                            <h3 id="usage-catalog-included-heading">Included uses per plan (each month)</h3>
                            <p className="usage-catalog-hint">
                                Uses each seat gets before points are spent. 0 means none included.
                                meetingAi.unit.xl can never be included.
                            </p>
                            <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-included-heading">
                                <table className="usage-catalog-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Rate key</th>
                                            {PLAN_GRANT_ORDER.map((planId) => (
                                                <th scope="col" key={planId}>{planName(planId)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {INCLUDED_UNIT_KEYS.map((key) => (
                                            <tr key={key} data-testid={`included-row-${key}`}>
                                                <th scope="row"><code>{key}</code></th>
                                                {PLAN_GRANT_ORDER.map((planId) => (
                                                    <td className="usage-catalog-num" key={planId}>
                                                        <input
                                                            id={`included-${planId}-${key}`}
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                            aria-label={`${planName(planId)} included ${key}`}
                                                            value={draft ? draft.included[planId][key] : ''}
                                                            onChange={(e) => updateIncluded(planId, key, e.target.value)}
                                                            disabled={inputsDisabled}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="usage-catalog-card" data-testid="usage-catalog-rates">
                            <h3 id="usage-catalog-rates-heading">Rates (points per unit)</h3>
                            <p className="usage-catalog-hint">
                                A new rate applies to new starts only. Reservations, quotes, jobs and refunds
                                already in flight keep the snapshot they were pinned to.
                            </p>
                            <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-rates-heading">
                                <table className="usage-catalog-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Rate key</th>
                                            <th scope="col">Unit</th>
                                            <th scope="col">Points</th>
                                            <th scope="col">Milli</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rateKeys.map((key) => {
                                            const badgeText = rateBadgeText(key, catalog && catalog.meteringSwitches);
                                            return (
                                                <tr key={key} data-testid={`rate-row-${key}`}>
                                                    <th scope="row">
                                                        <label htmlFor={`rate-${key}`}>{key}</label>
                                                        {badgeText && (
                                                            <p className="usage-catalog-hint" style={{ margin: '4px 0 0', whiteSpace: 'normal' }}>
                                                                {badgeText}
                                                            </p>
                                                        )}
                                                    </th>
                                                    <td>{RATE_UNITS[key] || 'unit'}</td>
                                                    <td className="usage-catalog-num">
                                                        <input
                                                            id={`rate-${key}`}
                                                            type="number"
                                                            min="0"
                                                            step="0.1"
                                                            value={draft ? draft.rates[key] : ''}
                                                            onChange={(e) => updateRate(key, e.target.value)}
                                                            disabled={inputsDisabled}
                                                        />
                                                    </td>
                                                    <td className="usage-catalog-num">
                                                        {draft ? String(toMilli(draft.rates[key])) : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {catalog && (
                            <div className="usage-catalog-card" data-testid="usage-catalog-guardrail">
                                <h3 id="usage-catalog-economics-heading">Economics guardrail</h3>
                                <p className="usage-catalog-hint">
                                    {`Costs come from the committed model run of ${usageEconomics.runDate} `}
                                    (<code>r3_flat_rate.py</code>
                                    {`, source ${String(usageEconomics.sourceSha).slice(0, 12)}). This panel only `}
                                    divides that output — it never recomputes a cost, so there is exactly one
                                    cost model.
                                </p>

                                {providerPricesAreStale && (
                                    <div role="alert" style={noticeStyle('warning')} data-testid="usage-catalog-stale">
                                        <AlertTriangle size={16} style={noticeIconStyle('warning')} aria-hidden="true" />
                                        <span>
                                            Provider prices changed — update the committed model inputs, rerun
                                            {' '}
                                            <code>python3 r3_flat_rate.py</code>
                                            {' '}
                                            and regenerate <code>usageEconomics.json</code> before trusting these numbers. Nothing on
                                            this screen has been recomputed.
                                        </span>
                                    </div>
                                )}

                                <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-economics-heading">
                                    <table className="usage-catalog-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Rate key</th>
                                                <th scope="col">Points</th>
                                                <th scope="col">Ordinary</th>
                                                <th scope="col">Worst allowed</th>
                                                <th scope="col">Aggressive</th>
                                                <th scope="col">All-fallback (display only)</th>
                                                <th scope="col">Verdict</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rateKeys.map((key) => {
                                                const points = pointsOf(key);
                                                const verdict = rateVerdict(key, points);
                                                const flagged = verdict === VERDICT_BELOW_COST;
                                                return (
                                                    <tr
                                                        key={key}
                                                        data-testid={`guardrail-row-${key}`}
                                                        className={flagged ? 'usage-catalog-flagged' : undefined}
                                                    >
                                                        <th scope="row">{key}</th>
                                                        <td className="usage-catalog-num">
                                                            {Number.isFinite(points) ? points : '—'}
                                                        </td>
                                                        {['ordinary', 'worst_allowed', 'aggressive', 'aggressive_all_fallback'].map((shape) => (
                                                            <td key={shape} className="usage-catalog-num">
                                                                {formatUsd(costPerPoint(key, shape, points))}
                                                            </td>
                                                        ))}
                                                        <td>
                                                            <span className="usage-catalog-verdict">
                                                                {flagged && (
                                                                    <AlertTriangle
                                                                        size={14}
                                                                        style={noticeIconStyle('warning')}
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                                {verdict}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <h4 id="usage-catalog-margins-heading">{`Plan worst-case margin — Modeled features only, ${usageEconomics.rateVersion} full-plan margin not verified`}</h4>
                                <p className="usage-catalog-hint">
                                    {`These margins price only the rate keys the ${usageEconomics.r3RunDate} `}
                                    {`flat-rate model run (${usageEconomics.rateVersion}) actually graded against a per-unit `}
                                    {'cost. Not modeled: '}
                                    {usageEconomics.ungradedRateKeys.join(', ')}
                                    {'. Server-side floors still apply to those keys '}
                                    {'(UsageCatalogValidator.MinimumSustainableMilli) — only this panel’s display of them is '}
                                    {'partial, not the guardrail itself.'}
                                </p>
                                <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-margins-heading">
                                    <table className="usage-catalog-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Plan</th>
                                                <th scope="col">Expected</th>
                                                <th scope="col">Worst allowed</th>
                                                <th scope="col">Aggressive</th>
                                                <th scope="col">Weakest channel</th>
                                                <th scope="col">Verdict</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.keys(usageEconomics.planMargins).map((plan) => {
                                                const margins = usageEconomics.planMargins[plan];
                                                const flagged = margins.worstAllowedMargin
                                                    < usageEconomics.thresholds.marginWorst;
                                                return (
                                                    <tr
                                                        key={plan}
                                                        data-testid={`plan-margin-row-${plan}`}
                                                        className={flagged ? 'usage-catalog-flagged' : undefined}
                                                    >
                                                        <th scope="row">{plan}</th>
                                                        <td className="usage-catalog-num">{formatPercent(margins.expectedMargin)}</td>
                                                        <td className="usage-catalog-num">{formatPercent(margins.worstAllowedMargin)}</td>
                                                        <td className="usage-catalog-num">{formatPercent(margins.aggressiveMargin)}</td>
                                                        <td>{margins.weakestChannel}</td>
                                                        <td>
                                                            <span className="usage-catalog-verdict">
                                                                {flagged && (
                                                                    <AlertTriangle
                                                                        size={14}
                                                                        style={noticeIconStyle('warning')}
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                                {flagged
                                                                    ? `Worst-case margin under ${formatPercent(usageEconomics.thresholds.marginWorst)}`
                                                                    : VERDICT_OK}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <h4 id="usage-catalog-worst-case-heading">{`Plan worst customer — ${usageEconomics.rateVersion} one-minute model`}</h4>
                                <p className="usage-catalog-hint">
                                    {'The heaviest customer each plan allows (most languages, translated captions on, '}
                                    {'the whole included basket used), graded at the model’s extra-language percent. '}
                                    {`Gates: worst allowed ≥ ${formatPercent(usageEconomics.thresholds.marginWorst)}, aggressive ≥ 0%.`}
                                </p>
                                <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-worst-case-heading">
                                    <table className="usage-catalog-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Plan</th>
                                                <th scope="col">Languages</th>
                                                <th scope="col">Minutes</th>
                                                <th scope="col">Worst allowed</th>
                                                <th scope="col">Aggressive</th>
                                                <th scope="col">Verdict</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.keys(usageEconomics.planWorstCase).map((planId) => {
                                                const worst = usageEconomics.planWorstCase[planId];
                                                const modeledPercent = usageEconomics.extraLanguagePercent[planId];
                                                const draftPercent = draft && draft.languagePercents[planId] !== undefined
                                                    ? toWholeNumber(draft.languagePercents[planId])
                                                    : modeledPercent;
                                                const graded = draftPercent === modeledPercent;
                                                const failedGate = worst.worstAllowedMargin < usageEconomics.thresholds.marginWorst
                                                    || worst.aggressiveMargin < 0;
                                                let verdict = VERDICT_OK;
                                                if (!graded) verdict = `${VERDICT_NOT_GRADED} at ${Number.isNaN(draftPercent) ? '—' : `${draftPercent}%`} (model: ${modeledPercent}%)`;
                                                else if (failedGate) verdict = 'Below the margin gate';
                                                const flagged = graded && failedGate;
                                                return (
                                                    <tr
                                                        key={planId}
                                                        data-testid={`plan-worst-case-row-${planId}`}
                                                        className={flagged ? 'usage-catalog-flagged' : undefined}
                                                    >
                                                        <th scope="row">{planName(planId)}</th>
                                                        <td className="usage-catalog-num">{worst.languages}</td>
                                                        <td className="usage-catalog-num">{worst.wallMinutes}</td>
                                                        <td className="usage-catalog-num">{formatPercent(worst.worstAllowedMargin)}</td>
                                                        <td className="usage-catalog-num">{formatPercent(worst.aggressiveMargin)}</td>
                                                        <td>
                                                            <span className="usage-catalog-verdict">
                                                                {flagged && (
                                                                    <AlertTriangle
                                                                        size={14}
                                                                        style={noticeIconStyle('warning')}
                                                                        aria-hidden="true"
                                                                    />
                                                                )}
                                                                {verdict}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <h4 id="usage-catalog-packs-heading">Pack ladder</h4>
                                <p className="usage-catalog-hint">
                                    {'Pack prices are set in App Store Connect and Stripe. This panel records '}
                                    {`the ladder the ${usageEconomics.r3RunDate} flat-rate model run (${usageEconomics.rateVersion}) priced, not today's `}
                                    {'storefront: the live SKUs and prices are PackProductCatalog.UsagePacks, and '}
                                    {'a rate change that retires or reprices a pack leaves these rows behind '}
                                    {'until the model is rerun. This panel changes no price either way.'}
                                </p>
                                <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-packs-heading">
                                    <table className="usage-catalog-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Product id</th>
                                                <th scope="col">Points</th>
                                                <th scope="col">Price</th>
                                                <th scope="col">Per point</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usageEconomics.packLadder.map((pack) => (
                                                <tr key={pack.productId}>
                                                    <th scope="row"><code>{pack.productId}</code></th>
                                                    <td className="usage-catalog-num">{pack.points}</td>
                                                    <td className="usage-catalog-num">{`$${pack.priceUsd.toFixed(2)}`}</td>
                                                    <td className="usage-catalog-num">{formatUsd(pack.grossPerPointUsd)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <h4 id="usage-catalog-providers-heading">Provider price inputs</h4>
                                <p className="usage-catalog-hint">
                                    These are the model run&apos;s own inputs, shown so an operator can see how
                                    old they are. Editing one changes no cost on this screen: the model is
                                    rerun outside the browser and its artifact republished.
                                </p>
                                <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-providers-heading">
                                    <table className="usage-catalog-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Provider</th>
                                                <th scope="col">Unit price</th>
                                                <th scope="col">Unit</th>
                                                <th scope="col">Kind</th>
                                                <th scope="col">As of</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usageEconomics.providerPrices.map((price) => (
                                                <tr key={price.name}>
                                                    <th scope="row">
                                                        <label htmlFor={`provider-price-${price.name}`}>{price.label}</label>
                                                    </th>
                                                    <td className="usage-catalog-num">
                                                        <input
                                                            id={`provider-price-${price.name}`}
                                                            data-testid={`provider-price-${price.name}`}
                                                            type="number"
                                                            min="0"
                                                            step="0.0001"
                                                            value={providerPrices[price.name]}
                                                            onChange={(e) => updateProviderPrice(price.name, e.target.value)}
                                                            disabled={inputsDisabled}
                                                        />
                                                    </td>
                                                    <td>{price.unit}</td>
                                                    <td>{price.kind}</td>
                                                    <td>{price.accessedAt}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {!readOnly && (
                                    <div className="usage-catalog-inline-actions">
                                        <button
                                            type="button"
                                            className="usage-catalog-secondary-btn"
                                            onClick={resetProviderPrices}
                                            disabled={!providerPricesAreStale}
                                        >
                                            <RotateCcw size={14} aria-hidden="true" />
                                            Reset to published inputs
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {!readOnly && (
                            <div className="usage-catalog-card">
                                <h3>Draft</h3>
                                <div className="usage-catalog-field">
                                    <label htmlFor="usage-catalog-effective-at">Effective at</label>
                                    <div className="usage-catalog-effective-row">
                                        <input
                                            id="usage-catalog-effective-at"
                                            type="datetime-local"
                                            value={draft ? utcIsoToLocalInput(draft.effectiveAtUtc) : ''}
                                            onChange={(e) => updateEffectiveAt(e.target.value)}
                                            disabled={inputsDisabled}
                                            aria-describedby="usage-catalog-effective-utc"
                                        />
                                        <button
                                            type="button"
                                            className="usage-catalog-secondary-btn"
                                            onClick={setEffectiveAtNow}
                                            disabled={inputsDisabled}
                                        >
                                            Now
                                        </button>
                                        <span id="usage-catalog-effective-utc" data-testid="usage-catalog-effective-utc">
                                            {draft ? formatUtcLabel(draft.effectiveAtUtc) : 'not set'}
                                        </span>
                                    </div>
                                    <small>
                                        {draft && draft.effectiveAtFollowsNow
                                            ? 'Now: the new version applies from the minute you save. Pick a later time to schedule it.'
                                            : 'Picked in your local time. New starts on or after this instant use the new version.'}
                                    </small>
                                </div>
                                {effectiveAtBlocked && (
                                    <div role="alert" style={noticeStyle('warning')} data-testid="usage-catalog-effective-past">
                                        <AlertTriangle size={16} style={noticeIconStyle('warning')} aria-hidden="true" />
                                        <span>{EFFECTIVE_AT_PAST_MESSAGE}</span>
                                    </div>
                                )}

                                {validation && (
                                    <div
                                        className="usage-catalog-validation"
                                        data-testid="usage-catalog-validation"
                                        aria-live="polite"
                                    >
                                        {validation.failed ? (
                                            <p className="usage-catalog-validation-headline">{validation.message}</p>
                                        ) : (
                                            <>
                                                <p className="usage-catalog-validation-headline">
                                                    {validation.valid
                                                        ? 'This draft passes validation.'
                                                        : 'This draft does not pass validation and cannot be published.'}
                                                </p>
                                                {validation.violations.length > 0 && (
                                                    <>
                                                        <h4>Violations</h4>
                                                        <ul>
                                                            {validation.violations.map((item) => <li key={item}>{item}</li>)}
                                                        </ul>
                                                    </>
                                                )}
                                                {validation.warnings.length > 0 && (
                                                    <>
                                                        <h4>Warnings</h4>
                                                        <ul>
                                                            {validation.warnings.map((item) => <li key={item}>{item}</li>)}
                                                        </ul>
                                                    </>
                                                )}
                                                {!reasonValid && (
                                                    <p className="usage-catalog-hint">
                                                        REASON_REQUIRED stays listed until a reason is entered in
                                                        Review changes — validation grades the reason too.
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {catalog && (
                            <div className="usage-catalog-card">
                                <h3 id="usage-catalog-history-heading">
                                    <History size={16} aria-hidden="true" />
                                    {' '}
                                    Change history
                                </h3>
                                <div className="usage-catalog-table-wrap" role="region" tabIndex={0} aria-labelledby="usage-catalog-history-heading">
                                    <table className="usage-catalog-table" data-testid="usage-catalog-audit">
                                        <thead>
                                            <tr>
                                                <th scope="col">When</th>
                                                <th scope="col">Who</th>
                                                <th scope="col">Field</th>
                                                <th scope="col">Reason</th>
                                                <th scope="col">Versions</th>
                                                <th scope="col">Trace</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {auditEntries.map((entry, index) => (
                                                // eslint-disable-next-line react/no-array-index-key -- audit
                                                // entries carry no stable id on the wire.
                                                <tr key={`${entry.atUtc}-${entry.field}-${index}`}>
                                                    <td>{formatTimestamp(entry.atUtc)}</td>
                                                    <td>{entry.adminUserId || '—'}</td>
                                                    <td>{entry.field || '—'}</td>
                                                    <td>{entry.reason || '—'}</td>
                                                    <td>{`${entry.catalogVersion || '—'} / ${entry.rateVersion || '—'}`}</td>
                                                    <td><code>{entry.traceId || '—'}</code></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {showEditor && !readOnly && (
                    // Sticky, not fixed: it stays on screen while this section scrolls without
                    // overlaying the rest of the Plans tab (MeetingLimitsSection already owns a
                    // fixed bar of its own). It keeps its DOM identity the whole time, so focus
                    // can return to the SAME Review button when the dialog closes.
                    <div className="usage-catalog-savebar" role="region" aria-label="Usage catalog draft">
                        <p className="usage-catalog-savebar-summary">
                            {anyDirty
                                ? `${changeCount} unsaved ${changeCount === 1 ? 'change' : 'changes'} in ${envLabel}`
                                : `No unsaved changes in ${envLabel}`}
                        </p>
                        <div className="usage-catalog-savebar-actions">
                            <button
                                type="button"
                                className="usage-catalog-secondary-btn"
                                onClick={handleValidate}
                                disabled={!target.resolved || !draft || isSaving || isValidating}
                            >
                                {isValidating ? 'Validating…' : 'Validate'}
                            </button>
                            <button
                                type="button"
                                className="usage-catalog-secondary-btn"
                                onClick={handleDiscard}
                                disabled={!target.resolved || !anyDirty || isSaving}
                            >
                                Discard
                            </button>
                            <button
                                type="button"
                                className="usage-catalog-review-btn"
                                ref={reviewBtnRef}
                                onClick={() => setReviewOpen(true)}
                                disabled={!target.resolved || !anyDirty || isSaving || effectiveAtBlocked}
                            >
                                Review changes
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {reviewOpen && (
                <div
                    className="usage-catalog-overlay"
                    // `!isSaving` for the same reason as the Escape branch above: a backdrop
                    // click mid-write would hide the only place the failure is reported.
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget && !isSaving) closeDialog(true);
                    }}
                >
                    <div
                        className="usage-catalog-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="usage-catalog-dialog-title"
                        ref={dialogRef}
                        onKeyDown={handleDialogKeyDown}
                    >
                        <h2 id="usage-catalog-dialog-title">Save and publish the points catalog?</h2>
                        <p>
                            {`These rates price every new start in ${envLabel}. Reservations, quotes, jobs and `}
                            refunds already in flight keep their pinned snapshot.
                        </p>

                        <div className="usage-catalog-diff" data-testid="usage-catalog-diff">
                            {diffRows.map((row) => (
                                <div className="usage-catalog-diff-row" key={row.field} data-testid={`diff-row-${row.field}`}>
                                    <span className="usage-catalog-diff-what">{row.label}</span>
                                    {/* An SVG icon, not a "→" text glyph: axe's color-contrast check cannot
                                        evaluate an element whose only content is a bare arrow character
                                        ("content contains only non-text characters"), and aria-hidden does
                                        not suppress that for a scoped run. Decorative only — the <s>/<b>
                                        already carry the old and new values in that visual order. */}
                                    <span className="usage-catalog-diff-vals">
                                        <s>{row.fromText}</s>
                                        <ArrowRight className="usage-catalog-diff-arrow" size={12} aria-hidden="true" />
                                        <b>{row.toText}</b>
                                    </span>
                                </div>
                            ))}
                            {diffRows.length === 0 && (
                                <p className="usage-catalog-diff-empty">
                                    {draftSaved
                                        ? 'The draft is saved. Nothing further has changed since.'
                                        : 'Nothing has changed yet.'}
                                </p>
                            )}
                        </div>

                        {dialogError && (
                            <div role="alert" style={noticeStyle('error')}>
                                <AlertCircle size={16} style={noticeIconStyle('error')} aria-hidden="true" />
                                <span>{dialogError}</span>
                            </div>
                        )}

                        {draftSaved && !dialogError && (
                            <div style={noticeStyle('success')}>
                                <CheckCircle size={16} style={noticeIconStyle('success')} aria-hidden="true" />
                                <span>The draft is saved.</span>
                            </div>
                        )}

                        <div className="usage-catalog-reason">
                            <label htmlFor="usage-catalog-reason">Reason for the change</label>
                            <textarea
                                id="usage-catalog-reason"
                                ref={reasonRef}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Example: provider price increase, see the 2026-09 pricing review."
                                disabled={isSaving}
                            />
                            <small>
                                Required, 5 to 500 characters. Saved to the change history with your name.
                            </small>
                        </div>

                        <div className="usage-catalog-dialog-actions">
                            <button type="button" onClick={() => closeDialog(true)} disabled={isSaving}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="usage-catalog-save-btn"
                                onClick={handleSaveAndPublish}
                                disabled={!target.resolved || isSaving || !reasonValid || !anyDirty || !everyValueParses || effectiveAtBlocked}
                            >
                                {isSaving ? 'Saving…' : 'Save draft & publish'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && <div className="usage-catalog-toast" role="status">{toast}</div>}
        </section>
    );
};

export default UsageCatalogSection;
